import { css } from '@compiled/react';

import { useLazyLoadQuery } from 'react-relay';
import { KUDOS_SUMMARY_QUERY } from 'graphql/KudosSummaryQuery';
import { KudosSummaryQuery } from 'graphql/__generated__/KudosSummaryQuery.graphql';
import { token } from '@atlaskit/tokens';
import { Kudos } from 'assets/images';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';
import useIsPersonalView from 'utils/useIsPersonalView';
import SubSection from 'components/common/section/sub-section/SubSection';
import EmptyStateMessageBody from 'components/common/section/sub-section/EmptyStateMessageBody';
import { ErrorBoundary } from 'react-error-boundary';
import { logBoundaryError } from 'services/logger';
import Fallback from 'components/common/fallback/Fallback';
import { PEER_KUDOS_DEFAULT_URL } from 'constants/index';
import { trackLearnMoreAboutKudosClick } from 'analytics/kudosAnalytics';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';
import { MOBILE, TABLET } from '../../../../constants/breakpoints';
import { LoadingMainPanelSection } from 'components/common/suspense-fallbacks/LoadingMainPanelSection';
import { Suspense } from 'react';

type Total = {
  comment: string;
  count: number | null | undefined;
};

type ProgressBarProps = {
  completed: number;
};

type PeerKudosTotals = {
  totals: Total[] | null | undefined;
};

export const KudosSummaryWrapper = ({ internalId }: { internalId: string }) => (
  <ErrorBoundary FallbackComponent={Fallback} onError={logBoundaryError}>
    <Suspense fallback={<LoadingMainPanelSection hasHeader={true} />}>
      <KudosSummary internalId={internalId} />
    </Suspense>
  </ErrorBoundary>
);

const KudosSummary = ({ internalId }: { internalId: string }) => {
  const kudosSummaryQuery = useLazyLoadQuery<KudosSummaryQuery>(
    KUDOS_SUMMARY_QUERY,
    { internalId },
    { fetchPolicy: 'store-or-network' },
  ).unified?.unifiedProfile?.gamification;
  const isPersonalView = useIsPersonalView(internalId);

  const recognitionSummaryTotals =
    kudosSummaryQuery?.recognitionsSummary as PeerKudosTotals; //real implementation
  // const recognitionSummaryTotals = dummyData as PeerKudosTotals; //used to view all recognition categories

  // Check if all totals are 0
  const allTotalsZero = recognitionSummaryTotals?.totals
    ? recognitionSummaryTotals.totals.every((total) => total.count === 0)
    : true;

  const levels = kudosSummaryQuery?.levels;

  return (
    <SectionSummaryContainer>
      <div css={mainContainerStyle}>
        <MainPanelSectionHeader
          testId="kudos-summary-title"
          title="Kudos"
          subtitle={
            isPersonalView ? (
              <span>
                {
                  'Get rewards for your activity and progress, from beginner to expert. '
                }
              </span>
            ) : undefined
          }
        />
        <div css={contentStyle} data-testid="kudos-parent-container">
          <KudosProgressSection
            data={{
              currentLevelArt: levels?.currentLevelArt,
              currentLevelDescription: levels?.currentLevelDescription,
              currentLevelName: levels?.currentLevelName,
              currentPoints: levels?.currentPoints,
              maxPoints: levels?.maxPoints,
              nextLevelName: levels?.nextLevelName,
            }}
          />
          {!isPersonalView && allTotalsZero ? null : (
            <PeerKudosReceivedSection
              data={recognitionSummaryTotals}
              isEmptyMessageShown={isPersonalView && allTotalsZero}
            />
          )}
        </div>
      </div>
    </SectionSummaryContainer>
  );
};

const PeerKudosReceivedSection = ({
  data,
  isEmptyMessageShown,
}: {
  data: PeerKudosTotals;
  isEmptyMessageShown: boolean;
}) => {
  const sendLearnMoreAnalytics = () => {
    trackLearnMoreAboutKudosClick();
  };

  function formatAce(inputString: string) {
    return inputString.replace(/\b(ace)\b/gi, 'ACE');
  }
  return (
    <div
      css={[sectionStyle, peerKudosReceivedTabletStyle]}
      data-testid="peer-kudos-received-container"
    >
      <SubSection title="Peer Kudos Received">
        {isEmptyMessageShown ? (
          <EmptyStateMessageBody
            message="Earn and give kudos for helping others. Kudos you receive will appear here."
            linkTo={PEER_KUDOS_DEFAULT_URL}
            linkType="external"
            sendAnalyticsEvent={sendLearnMoreAnalytics}
            linkText="Learn more about kudos"
          />
        ) : (
          data?.totals?.map(
            (total, index) =>
              total.count !== 0 && (
                <span key={index} css={kudosLinkCardTitleStyle}>
                  <img css={[kudosIconStyle]} src={Kudos}></img>
                  <span css={kudosCardStyle}>{`${total.count} kudos ${formatAce(
                    total.comment,
                  )}`}</span>
                </span>
              ),
          )
        )}
      </SubSection>
    </div>
  );
};

const KudosProgressSection = ({
  data,
}: {
  data: {
    currentLevelArt: string | null | undefined;
    currentLevelDescription: string | null | undefined;
    currentLevelName: string | null | undefined;
    currentPoints: number | null | undefined;
    maxPoints: number | null | undefined;
    nextLevelName: string | null | undefined;
  };
}) => {
  const currentPoints = data.currentPoints ?? 0;
  const maxPoints = data.maxPoints ?? 1;

  return (
    <div css={sectionStyle}>
      <SubSection title="Progress">
        <div>
          <div css={progressFrameStyle}>
            {data.currentLevelArt && (
              <img css={progressLevelIconStyle} src={data.currentLevelArt} />
            )}
            <div css={progressLevelStyle}>
              <div css={progressLevelTitleStyle}>
                <h6
                  css={progressFrameLevelTextStyle}
                >{`${data.currentLevelDescription}: ${data.currentLevelName}`}</h6>
              </div>
              <ProgressBar completed={(currentPoints / maxPoints) * 100} />
              <div css={progressFrameLowerStyle}>
                <span
                  css={progressLowerNextTextStyle}
                >{`Next: ${data.nextLevelName}`}</span>
                <span
                  css={progressLowerPointsTextStyle}
                >{`${currentPoints.toLocaleString(
                  'en-US',
                )} / ${maxPoints.toLocaleString('en-US')} points`}</span>
              </div>
            </div>
          </div>
        </div>
      </SubSection>
    </div>
  );
};

const ProgressBar = ({ completed }: ProgressBarProps) => {
  return (
    <div css={progressBarContainerStyle}>
      <div css={progressBarStyles} style={{ width: `${completed}%` }}></div>
    </div>
  );
};

const progressBarStyles = css({
  height: '100%',
  backgroundColor: token('color.background.accent.blue.bolder'),
  textAlign: 'right',
});
const kudosCardStyle = css({
  //layout
  height: '22px',
  //typography
  color: token('color.text'),
  fontFamily: 'Inter',
  fontSize: token('space.200'), //16px
  fontStyle: 'normal',
  fontWeight: token('font.weight.regular'), //400
  lineHeight: token('space.300'), //24px
});

const kudosLinkCardTitleStyle = css({
  display: 'flex',
  gap: token('space.200'),
  alignItems: 'center',
});

const kudosIconStyle = css({
  width: '29px',
  height: '29px',
});

const progressFrameLevelTextStyle = css({
  color: token('color.text'),
  fontFamily: token('font.family.brand.heading'),
  fontStyle: 'normal',
  textTransform: 'none',
  fontWeight: token('font.weight.semibold'), //600
  fontSize: token('space.200'),
  lineHeight: token('space.400'),
});

const progressLowerNextTextStyle = css({
  color: token('color.text.subtle'),
  textAlign: 'left',
  fontFamily: 'Inter',
  fontStyle: 'normal',
  textTransform: 'none',
  fontWeight: token('font.weight.regular'),
  fontSize: '14px',
  lineHeight: token('space.250'), //20px
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    lineHeight: '22px',
  },
});

const progressLowerPointsTextStyle = css({
  color: token('color.text.subtle'),
  textAlign: 'right',
  fontFamily: 'Inter',
  fontStyle: 'normal',
  textTransform: 'none',
  fontWeight: token('font.weight.regular'),
  fontSize: '14px',
  lineHeight: token('space.250'),
  flex: '1 0 0',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    lineHeight: '22px',
  },
});

const progressLevelTitleStyle = css({
  flexDirection: 'row',
  display: 'flex',
  alignItems: 'flex-start',
  gap: token('space.050'),
  width: '100%',
});
const progressLevelIconStyle = css({
  width: token('space.800'), //64px
  height: token('space.800'),
  flexShrink: 0,
  alignSelf: 'flex-start',
});
const progressFrameStyle = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

const progressFrameLowerStyle = css({
  display: 'flex',
  width: '241px',
  alignItems: 'flex-start',
  gap: token('space.200'), //16px
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    maxWidth: '231px',
  },
});

const progressLevelStyle = css({
  width: '241px',
  flexShrink: 0,
  marginLeft: token('space.200'),
});

//could use elsewhere for progress bar styles
const progressBarContainerStyle = css({
  height: token('space.100'), //8px
  width: '100%',
  backgroundColor: token('color.background.accent.gray.subtlest'),
  borderRadius: token('space.050'), //4px
  overflow: 'hidden',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    maxWidth: '231px',
  },
});

//should be the same for all border boxes on main column of profile
const contentStyle = css({
  //layout
  display: 'flex',
  alignItems: 'flex-start',
  gap: `${token('space.600')}`,
  alignSelf: 'stretch',
  //style
  borderRadius: token('space.100'),
  background: token('color.background.input'),
  boxShadow: token('elevation.shadow.raised'),
  padding: `${token('space.400')} ${token('space.300')}`,

  '@media(max-width: 767px)': {
    padding: `${token('space.300')} ${token('space.200')}`,
    flexDirection: 'column',
    gap: token('space.400'),
    flexShrink: 0,
  },
});

const sectionStyle = css({
  width: '50%',
  '@media(max-width: 767px)': {
    width: '100%',
  },
});

const peerKudosReceivedTabletStyle = css({
  [TABLET.MEDIA_QUERY]: {
    // Due to the negative margin on the avatars, and the width of progress bar on tablet
    // We need this negative margin so 'Peer Kudos Received aligns with Most Recent Badges'
    // On tablet views
    marginLeft: '-7px',
  },
});

//should be the same for all containers on the main column of profile
const mainContainerStyle = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  '@media (max-width: 767px)': {
    gap: token('space.200'),
  },
});

export default KudosSummary;
